// The lightbox frame every window uses (legacy window/abstract.js): overlay (optional), the window
// box with the legacy classes `lbx_window lbx_<name> lbx_showed lbx_ads_big|small`, a close button
// (`a.lbx_close[data-action=close]`, no duplicate ids), header and content. A window opens at once
// (legacy had no open animation); the close removes lbx_showed and animates (350 ms / 400 ms). Windows are portaled into <body> like legacy.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type React from 'react';
import { createPortal } from 'react-dom';
import { env } from '../env';
import { uiStore, type WindowName } from '../store/uiStore';
import './windows.css';
import './win.css'; // the Win window's celebration, after windows.css on purpose
import './ads.css'; // the ad boxes of every window, last on purpose

export interface WindowFrameProps {
  name: WindowName;
  header: ReactNode;
  children: ReactNode;
  overlay?: boolean;
  closable?: boolean;
  closing: boolean;
  /** Extra class names on the window box, after the legacy ones (e.g. no-ads). */
  className?: string;
  /** `data-mode` on the window box (the Auth window's login/register look). */
  mode?: string;
  onClose?: (() => void) | undefined;
  /** Show a minimize button (rooms window): hides the window, keeps it alive. */
  minimizable?: boolean;
  minimized?: boolean | undefined;
}

/** Ad slots and classes: off for the offline target with aat=off (legacy), and for a dev server started
 *  with VITE_NO_ADS=1 (LAN testing on phones; never set in a production build). */
export const adsEnabled = (): boolean =>
  !(env.appTarget === 'offline' && env.adsType === 'off') && import.meta.env.VITE_NO_ADS !== '1';
export const screenClass = (): 'big' | 'small' => (innerWidth >= 768 && innerHeight >= 500 ? 'big' : 'small');

/**
 * Desktop: the window can be dragged by its header (legacy abstract.js); the offset is a transform
 * and the box never leaves the viewport (GAP-016 fixed). Touch and pen are left to scrolling.
 */
function useHeaderDrag(boxRef: React.RefObject<HTMLDivElement | null>) {
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ startX: number; startY: number; x0: number; y0: number; base: DOMRect } | null>(null);
  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (e.pointerType !== 'mouse' || e.button !== 0 || !boxRef.current) return;
    // a press just beside the close (or minimize) button is a missed click on it, not a drag (owner 2026-10-23:
    // "the X did not take the click")
    for (const btn of boxRef.current.querySelectorAll<HTMLElement>('.lbx_close, .lbx_minimize')) {
      const r = btn.getBoundingClientRect();
      if (e.clientX >= r.left - 8 && e.clientX <= r.right + 8 && e.clientY >= r.top - 8 && e.clientY <= r.bottom + 8) {
        btn.click();
        e.preventDefault();
        return;
      }
    }
    // The layout box (offset* ignore transforms: the open animation may still scale the window).
    const b = boxRef.current;
    drag.current = {
      startX: e.clientX,
      startY: e.clientY,
      x0: offset.x,
      y0: offset.y,
      base: new DOMRect(b.offsetLeft, b.offsetTop, b.offsetWidth, b.offsetHeight),
    };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
    setDragging(true);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    let x = d.x0 + e.clientX - d.startX;
    let y = d.y0 + e.clientY - d.startY;
    const { base } = d;
    x = Math.min(Math.max(x, -base.left), innerWidth - base.right);
    y = Math.min(Math.max(y, -base.top), innerHeight - base.bottom);
    setOffset({ x, y });
  };
  const onPointerUp = () => {
    drag.current = null;
    setDragging(false);
  };
  // A dragged window is re-centred when the viewport changes (the offset was clamped to the old one).
  useEffect(() => {
    const reset = () => setOffset({ x: 0, y: 0 });
    addEventListener('resize', reset);
    return () => removeEventListener('resize', reset);
  }, []);
  return { offset, dragging, handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp } };
}

export function WindowFrame(p: WindowFrameProps) {
  // Like legacy, a window appears at once with lbx_showed set (no scale-in): only the close animates.
  // Opening from scale(0.1) also left the text laid out half a pixel off the legacy screenshots.
  const shown = true;
  const boxRef = useRef<HTMLDivElement>(null);
  const { offset, dragging, handlers } = useHeaderDrag(boxRef);
  const close = () => {
    p.onClose?.();
    uiStore().closeWindow(p.name);
  };
  // Legacy class order (tests pin it): lbx_window lbx_showed lbx_ads_big|small lbx_<name> [extra].
  const classes = [
    'lbx_window',
    shown && !p.closing ? 'lbx_showed' : '',
    adsEnabled() ? `lbx_ads_${screenClass()}` : '',
    `lbx_${p.name}`,
    p.className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return createPortal(
    <>
      {p.minimized ? null : <div className={p.overlay ? 'lbx_overlay' : 'lbx_veil'} data-closing={p.closing || undefined} />}
      <div
        ref={boxRef}
        className={classes}
        data-window={p.name}
        data-mode={p.mode}
        role="dialog"
        aria-modal={p.overlay || undefined}
        data-dragging={dragging || undefined}
        style={
          p.minimized
            ? { display: 'none' }
            : offset.x || offset.y
              ? ({ '--drag-x': `${offset.x}px`, '--drag-y': `${offset.y}px` } as React.CSSProperties)
              : undefined
        }
      >
        {p.closable !== false ? (
          <a
            href="#"
            className="lbx_close"
            data-action="close"
            aria-label="Close"
            onClick={(e) => {
              e.preventDefault();
              close();
            }}
          />
        ) : null}
        {p.minimizable ? (
          <a
            href="#"
            id="minimize"
            className="lbx_minimize"
            data-action="minimize"
            aria-label="Minimize"
            onClick={(e) => {
              e.preventDefault();
              uiStore().minimizeWindow(p.name);
            }}
          />
        ) : null}
        <div className="lbx_container">
          <span className="lbx_header" {...handlers}>
            {p.header}
          </span>
          <div className="lbx_content">{p.children}</div>
        </div>
      </div>
    </>,
    document.body,
  );
}

/** Tabs like legacy Solitaire.Tab: `.tab_buttons a.tab_selected` + `.tab_contents` shown by display. */
export function Tabs({
  labels,
  initial = 0,
  children,
  onChange,
}: {
  labels: ReactNode[];
  initial?: number;
  children: ReactNode[];
  onChange?: (i: number) => void;
}) {
  const [active, setActive] = useState(initial);
  return (
    <>
      <div className="tab_buttons">
        {labels.map((label, i) => (
          <a
            key={i}
            href="#"
            className={i === active ? 'tab_selected' : ''}
            onClick={(e) => {
              e.preventDefault();
              setActive(i);
              onChange?.(i);
            }}
          >
            {label}
          </a>
        ))}
      </div>
      {children.map((child, i) => (
        <div key={i} className="tab_contents" style={{ display: i === active ? 'block' : 'none' }}>
          {child}
        </div>
      ))}
    </>
  );
}
